import React from 'react';
import { getSupabaseClient } from '../supabaseClient';

type AnalyticsData = {
  totalUsers: number;
  totalBooks: number;
  totalRequests: number;
  activeRequests: number;
  completedRequests: number;
  monthlyGrowth: { month: string; users: number; books: number; requests: number }[];
  topCategories: { category: string; count: number }[];
  userActivity: { period: string; activeUsers: number; newUsers: number }[];
  requestTrends: { status: string; count: number; percentage: number }[];
};

export default function Analytics() {
  const supabase = getSupabaseClient();
  const [analytics, setAnalytics] = React.useState<AnalyticsData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [timeRange, setTimeRange] = React.useState('30d');

  const loadAnalytics = React.useCallback(async () => {
    setLoading(true);
    try {
      // Load basic counts
      const [
        { count: usersCount },
        { count: booksCount },
        { count: requestsCount },
        { count: activeRequestsCount },
        { count: completedRequestsCount },
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('books').select('*', { count: 'exact', head: true }),
        supabase.from('requests').select('*', { count: 'exact', head: true }),
        supabase.from('requests').select('*', { count: 'exact', head: true }).neq('status', 'closed').neq('status', 'returned'),
        supabase.from('requests').select('*', { count: 'exact', head: true }).eq('status', 'closed'),
      ]);

      // Load categories data
      const { data: categoriesData } = await supabase
        .from('books')
        .select('category')
        .not('category', 'is', null);

      const categoryCounts = categoriesData?.reduce((acc: Record<string, number>, book: any) => {
        if (book.category) {
          acc[book.category] = (acc[book.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>) || {};

      const topCategories = Object.entries(categoryCounts)
        .map(([category, count]) => ({ category, count: count as number }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Load request trends
      const { data: requestsData } = await supabase
        .from('requests')
        .select('status');

      const requestStatusCounts = requestsData?.reduce((acc: Record<string, number>, request: any) => {
        acc[request.status] = (acc[request.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const totalRequests = requestsData?.length || 0;
      const requestTrends = Object.entries(requestStatusCounts).map(([status, count]) => ({
        status,
        count: count as number,
        percentage: totalRequests > 0 ? Math.round((count as number / totalRequests) * 100) : 0,
      }));

      // Generate sample monthly growth data (in real app, you'd query by date ranges)
      const monthlyGrowth = [
        { month: 'Jan', users: 50, books: 120, requests: 30 },
        { month: 'Feb', users: 75, books: 180, requests: 45 },
        { month: 'Mar', users: 100, books: 250, requests: 60 },
        { month: 'Apr', users: 120, books: 300, requests: 75 },
        { month: 'May', users: 150, books: 380, requests: 95 },
        { month: 'Jun', users: 180, books: 450, requests: 110 },
      ];

      // Generate sample user activity data
      const userActivity = [
        { period: 'Last 7d', activeUsers: 45, newUsers: 12 },
        { period: 'Last 30d', activeUsers: 120, newUsers: 35 },
        { period: 'Last 90d', activeUsers: 280, newUsers: 85 },
      ];

      setAnalytics({
        totalUsers: usersCount || 0,
        totalBooks: booksCount || 0,
        totalRequests: requestsCount || 0,
        activeRequests: activeRequestsCount || 0,
        completedRequests: completedRequestsCount || 0,
        monthlyGrowth,
        topCategories,
        userActivity,
        requestTrends,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  React.useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending_owner_reply': 'Pending Reply',
      'info_gathering': 'Info Gathering',
      'replied': 'Replied',
      'rejected': 'Rejected',
      'payment_pending': 'Payment Pending',
      'paid': 'Paid',
      'shipped': 'Shipped',
      'received': 'Received',
      'being_read': 'Being Read',
      'returned': 'Returned',
      'closed': 'Closed',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div>
        <h1 className="page-header">
          Analytics & Reports
        </h1>
        <div className="page-subtitle">
          Loading analytics data...
        </div>
        <div className="card">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div>
        <h1 className="page-header">
          Analytics & Reports
        </h1>
        <div className="card">
          <div>Failed to load analytics data</div>
          <button onClick={loadAnalytics} style={{ marginTop: '12px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-header">
        Analytics & Reports
      </h1>
      <div className="page-subtitle">
        Platform statistics and performance metrics
      </div>

      {/* Time Range Selector */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>Overview</h2>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="filter-select"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>

        {/* Key Metrics */}
        <div className="stats-grid">
          <div className="metric-card">
            <div className="metric-value stats-card-value-primary">
              {analytics.totalUsers}
            </div>
            <div className="metric-label">Total Users</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-success">
              {analytics.totalBooks}
            </div>
            <div className="metric-label">Total Books</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-warning">
              {analytics.totalRequests}
            </div>
            <div className="metric-label">Total Requests</div>
          </div>
          <div className="metric-card">
            <div className="metric-value stats-card-value-info">
              {analytics.activeRequests}
            </div>
            <div className="metric-label">Active Requests</div>
          </div>
        </div>
      </div>

      <div className="two-column-grid">
        {/* Top Categories */}
        <div className="card">
          <h2>Top Categories</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {analytics.topCategories.map((category, index) => (
              <div key={category.category} className="list-item">
                <span className="list-item-label">
                  {index + 1}. {category.category}
                </span>
                <span className="badge-count badge-count-positive">
                  {category.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* User Activity */}
        <div className="card">
          <h2>User Activity</h2>
          <div>
            {analytics.userActivity.map((activity) => (
              <div key={activity.period} className="activity-item stats-grid">
                <div>
                  <div className="activity-description">{activity.period}</div>
                  <div className="activity-timestamp">
                    {activity.newUsers} new users
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="list-item-value">
                    {activity.activeUsers}
                  </div>
                  <div className="activity-timestamp">active users</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Request Trends */}
      <div className="card">
        <h2>Request Status Distribution</h2>
        <div className="stats-grid">
          {analytics.requestTrends.map((trend) => (
            <div key={trend.status} className="card" style={{ padding: '12px', background: '#f8f9fa' }}>
              <div className="list-item">
                <span className="list-item-label">{getStatusLabel(trend.status)}</span>
                <span className="list-item-value">{trend.count}</span>
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${trend.percentage}%` }}
                />
              </div>
              <div className="activity-timestamp" style={{ marginTop: '4px' }}>
                {trend.percentage}% of total requests
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Growth */}
      <div className="card">
        <h2>Monthly Growth</h2>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr className="table-header-row">
                <th className="table-cell">Month</th>
                <th className="table-cell" style={{ textAlign: 'right' }}>Users</th>
                <th className="table-cell" style={{ textAlign: 'right' }}>Books</th>
                <th className="table-cell" style={{ textAlign: 'right' }}>Requests</th>
                <th className="table-cell" style={{ textAlign: 'right' }}>Growth %</th>
              </tr>
            </thead>
            <tbody>
              {analytics.monthlyGrowth.map((month, index) => {
                const prevMonth = index > 0 ? analytics.monthlyGrowth[index - 1] : null;
                const growthPercentage = prevMonth
                  ? Math.round(((month.users - prevMonth.users) / prevMonth.users) * 100)
                  : 0;

                return (
                  <tr key={month.month} className="table-data-row">
                    <td className="table-cell" style={{ fontWeight: '500' }}>{month.month}</td>
                    <td className="table-cell" style={{ textAlign: 'right' }}>{month.users}</td>
                    <td className="table-cell" style={{ textAlign: 'right' }}>{month.books}</td>
                    <td className="table-cell" style={{ textAlign: 'right' }}>{month.requests}</td>
                    <td className="table-cell" style={{
                      textAlign: 'right',
                      color: growthPercentage >= 0 ? '#28a745' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      {growthPercentage > 0 ? '+' : ''}{growthPercentage}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Options */}
      <div className="card">
        <h2>Export Reports</h2>
        <div className="stats-grid">
          <button>Export Users (CSV)</button>
          <button>Export Books (CSV)</button>
          <button>Export Requests (CSV)</button>
          <button>Export Analytics (PDF)</button>
        </div>
      </div>
    </div>
  );
}